<template>
  <div class="text-link-input-wrapper">
    <!-- 메인 인풋 영역 -->
    <div 
      class="text-link-input" 
      :class="{ readonly: readonly, disabled: disabled }"
      @click="openPopup"
    >
      <input 
        type="text" 
        :value="displayText" 
        :placeholder="placeholder"
        readonly
        class="input-field"
      />
      <!-- 링크 아이콘 (링크가 있을 때만 표시) -->
      <a 
        v-if="hasLink" 
        :href="linkValue" 
        target="_blank" 
        class="link-icon"
        @click.stop
      >
        <feather-icon icon="ExternalLinkIcon" />
      </a>
    </div>

    <!-- 팝업 -->
    <vs-popup 
      class="text-link-popup" 
      title="텍스트 및 링크 입력" 
      :active.sync="showPopup"
    >
      <div class="popup-content">
        <div class="input-group">
          <label>텍스트</label>
          <vs-input 
            class="w-full" 
            v-model="tempText" 
            placeholder="텍스트를 입력하세요"
          />
        </div>
        <div class="input-group">
          <label>링크 (URL)</label>
          <vs-input 
            class="w-full" 
            v-model="tempLink" 
            placeholder="https://example.com"
          />
          <p v-if="tempLink && !isValidUrl" class="error-text">올바른 URL 형식이 아닙니다.</p>
        </div>
      </div>
      <div class="popup-footer">
        <vs-button type="border" @click="cancelPopup">취소</vs-button>
        <vs-button @click="savePopup">저장</vs-button>
      </div>
    </vs-popup>
  </div>
</template>

<script>
export default {
  name: 'TextLinkInput',
  props: {
    // v-model로 { text: '', link: '' } 객체를 받음
    value: {
      type: [Object, String],
      default: () => ({ text: '', link: '' })
    },
    placeholder: {
      type: String,
      default: '클릭하여 입력'
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showPopup: false,
      tempText: '',
      tempLink: ''
    }
  },
  computed: {
    // 표시할 텍스트
    displayText() {
      if (typeof this.value === 'string') {
        return this.value
      }
      return (this.value && this.value.text) || ''
    },
    // 링크 값
    linkValue() {
      if (typeof this.value === 'string') {
        return ''
      }
      return (this.value && this.value.link) || ''
    },
    // 링크가 있는지 여부
    hasLink() {
      return !!this.linkValue
    },
    // URL 유효성 검사
    isValidUrl() {
      if (!this.tempLink) return true
      const pattern = new RegExp(
        '^(https?:\\/\\/)?' +
        '((([a-zA-Z\\d\\uac00-\\ud7af]([a-zA-Z\\d\\uac00-\\ud7af-]*[a-zA-Z\\d\\uac00-\\ud7af])*)\\.)+[a-zA-Z\\d\\uac00-\\ud7af]{2,}|' +
        '((\\d{1,3}\\.){3}\\d{1,3})|' +
        '([\\w-]+\\.)*[\\w-]+\\.[\\w-]{2,})' +
        '(\\:\\d+)?(\\/[-a-zA-Z\\d%_.~+\\u00a1-\\uffff\\uac00-\\ud7af]*)*' +
        '(\\?[;&a-zA-Z\\d%_.~+=-]*)?' +
        '(\\#[-a-zA-Z\\d_]*)?$',
        'i'
      )
      return !!pattern.test(this.tempLink)
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (typeof val === 'string') {
          this.tempText = val
          this.tempLink = ''
        } else {
          this.tempText = (val && val.text) || ''
          this.tempLink = (val && val.link) || ''
        }
      }
    }
  },
  methods: {
    openPopup() {
      if (this.readonly || this.disabled) return
      
      // 현재 값으로 임시 값 초기화
      if (typeof this.value === 'string') {
        this.tempText = this.value
        this.tempLink = ''
      } else {
        this.tempText = (this.value && this.value.text) || ''
        this.tempLink = (this.value && this.value.link) || ''
      }
      this.showPopup = true
    },
    cancelPopup() {
      this.showPopup = false
    },
    savePopup() {
      // 링크에 프로토콜이 없으면 추가
      let link = this.tempLink
      if (link && !link.includes('http://') && !link.includes('https://')) {
        link = 'https://' + link
      }
      
      this.$emit('input', {
        text: this.tempText,
        link: link
      })
      this.showPopup = false
    }
  }
}
</script>

<style lang="scss" scoped>
.text-link-input-wrapper {
  width: 100%;
}

.text-link-input {
  display: flex;
  align-items: center;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  background: #fff;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(.readonly):not(.disabled) {
    border-color: rgba(var(--vs-primary), 0.5);
  }

  &:focus-within:not(.readonly):not(.disabled) {
    border: 1px solid rgba(var(--vs-primary), 1);
    box-shadow: 0 3px 10px 0 rgb(0 0 0 / 15%);
  }

  &.readonly {
    background-color: #fafafa;
    cursor: default;
  }

  &.disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.6;
  }

  .input-field {
    flex: 1;
    border: none;
    background: transparent;
    padding: 0.7rem 1rem;
    font-size: 1rem;
    cursor: inherit;
    outline: none;
    
    &::placeholder {
      color: #b4b4b4;
    }
  }

  .link-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    margin-right: 0.5rem;
    color: #7E72F2;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.2s;

    &:hover {
      background-color: rgba(126, 114, 242, 0.1);
    }
  }
}

.popup-content {
  padding: 1rem 0;

  .input-group {
    margin-bottom: 1rem;

    label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 500;
      color: #333;
    }

    .error-text {
      margin-top: 0.25rem;
      font-size: 0.75rem;
      color: #ea5455;
    }
  }
}

.popup-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;
}
</style>

<style>
.text-link-popup .vs-popup {
  width: 400px !important;
  max-width: 90vw;
}
</style>
